import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';

interface Group {
  id: string;
  displayName: string;
  mail: string;
}

interface SignatureAssignmentProps {
  signatureId: string;
  assignments: string[];
  onAssignmentChange: (groups: string[]) => void;
  onBack: () => void;
  onSave: (groups: string[]) => Promise<void>;
}

const SignatureAssignment: React.FC<SignatureAssignmentProps> = ({
  signatureId,
  assignments,
  onAssignmentChange,
  onBack,
  onSave
}) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>(assignments || []);
  const [initialGroups] = useState<string[]>(assignments || []);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const { instance, accounts } = useMsal();

  const hasChanges = JSON.stringify([...selectedGroups].sort()) !== JSON.stringify([...initialGroups].sort());

  useEffect(() => {
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAccessToken = async () => {
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['User.Read.All', 'Group.Read.All'],
        account: accounts[0]
      });
      return response.accessToken;
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  };

  const fetchGroups = async () => {
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        console.error('No access token available');
        setLoading(false);
        return;
      }

      const groupsResponse = await fetch('/api/graph/groups', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (groupsResponse.ok) {
        const groupsData = await groupsResponse.json();
        setGroups(groupsData.groups || []);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupToggle = (groupId: string) => {
    const newSelectedGroups = selectedGroups.includes(groupId)
      ? selectedGroups.filter(id => id !== groupId)
      : [...selectedGroups, groupId];
    
    setSelectedGroups(newSelectedGroups);
    // Don't call onAssignmentChange here - only update on Save
  };

  const handleRemoveGroup = (groupId: string) => {
    setSelectedGroups(selectedGroups.filter(id => id !== groupId));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(selectedGroups);
    } catch (error) {
      console.error('Error saving assignments:', error);
    } finally {
      setSaving(false);
    }
  };

  const filteredGroups = groups.filter(group =>
    group.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="assignment-loading">Loading groups...</div>;
  }

  return (
    <div className="signature-assignment">
      <div className="assignment-header">
        <button className="btn-back" onClick={onBack}>
          ← Back
        </button>
        <h3>Assign to Groups</h3>
      </div>
      
      <div className="assignment-info">
        {selectedGroups.length === 0 ? (
          <p className="no-assignments">No groups assigned</p>
        ) : (
          <div className="assigned-groups-list">
            <p className="assigned-label">Assigned to:</p>
            <div className="assigned-tags">
              {selectedGroups.map(groupId => {
                const group = groups.find(g => g.id === groupId);
                return group ? (
                  <div key={groupId} className="assigned-tag">
                    <span>{group.displayName}</span>
                    <button 
                      className="remove-tag-btn"
                      onClick={() => handleRemoveGroup(groupId)}
                      disabled={saving}
                      title="Remove assignment"
                    >
                      ✕
                    </button>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        )}
      </div>

      <div className="search-box">
        <input
          type="text"
          placeholder="Search groups..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={saving}
        />
      </div>

      <div className="assignment-list">
        {filteredGroups.length === 0 ? (
          <div className="no-results">No groups found</div>
        ) : (
          filteredGroups.map(group => (
            <div key={group.id} className="assignment-item">
              <label>
                <input
                  type="checkbox"
                  checked={selectedGroups.includes(group.id)}
                  onChange={() => handleGroupToggle(group.id)}
                  disabled={saving}
                />
                <span className="group-name">{group.displayName}</span>
                {group.mail && <span className="group-email">{group.mail}</span>}
              </label>
            </div>
          ))
        )}
      </div>

      <div className="assignment-actions">
        <button 
          className="btn-secondary" 
          onClick={onBack}
          disabled={saving}
        >
          Cancel
        </button>
        <button 
          className="btn-primary" 
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          {saving ? 'Saving...' : 'Save Assignments'}
        </button>
      </div>
    </div>
  );
};

export default SignatureAssignment;
